import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faChevronDown,
	faCircleMinus,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { CellAssignment, PersonaView, SegmentView } from "../scoreConfigView";
import { useFlashOnFocus } from "../useFocusTarget";

import { cellState, ScoringOption } from "./matrixModel";
import { ScoringPicker } from "./ScoringPicker";

export interface MatrixCellActions {
	onPick: (segment: SegmentView, persona: PersonaView, criteriaId: number) => void;
	onClear: (segment: SegmentView, persona: PersonaView) => void;
	onDeactivate: (segment: SegmentView, persona: PersonaView) => void;
	onCreate: (segment: SegmentView, persona: PersonaView) => void;
	onEdit: (criteriaId: number) => void;
}

interface MatrixCellProps extends MatrixCellActions {
	segment: SegmentView;
	persona: PersonaView;
	linked: boolean;
	assignment: CellAssignment | null;
	options: ScoringOption[];
	/** Tinted when more than one active scoring is connected to the cell (PREC-3946). */
	ambiguous: boolean;
	flash: boolean;
	onFlashDone: () => void;
}

/**
 * One matrix cell (PREC-4055) in one of three states: the scoring it holds, a
 * red "Missing Lead Scoring", or an amber "combination does not exist". Every
 * state opens the same picker.
 */
export const MatrixCell: FunctionComponent<MatrixCellProps> = ({
	segment,
	persona,
	linked,
	assignment,
	options,
	ambiguous,
	flash,
	onFlashDone,
	onPick,
	onClear,
	onDeactivate,
	onCreate,
	onEdit,
}) => {
	const { t } = useTranslation();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);
	const state = cellState(linked, assignment);
	const swatch = assignment
		? options.find((option) => option.id === assignment.criteriaId)?.swatch
		: undefined;

	useFlashOnFocus(buttonRef, flash, onFlashDone);

	const button = (
		<button
			ref={buttonRef}
			className={classNames(
				"flex h-7 w-full cursor-pointer items-center gap-1.5 rounded-md border px-2 text-left text-xs",
				state === "assigned" &&
					"border-border-light bg-white text-text hover:border-brand-light hover:bg-blue-action-200",
				state === "missing" &&
					"border-status-error-light bg-error-light text-status-error hover:border-status-error",
				state === "none" &&
					"border-dashed border-tag-warning-border bg-background-warning text-tag-warning-dark hover:border-solid",
				ambiguous && "ring-1 ring-status-warning"
			)}
			data-testid={`matrix-cell-${segment.key}-${persona.id}`}
			type="button"
			onClick={() => setOpen((current) => !current)}
		>
			{state === "assigned" && (
				<span className={classNames("h-2 w-2 shrink-0 rounded-full", swatch)} />
			)}
			{state === "missing" && <Icon className="h-3 w-3 shrink-0" icon={faTriangleExclamation} />}
			{state === "none" && <Icon className="h-3 w-3 shrink-0" icon={faCircleMinus} />}
			<span className="min-w-0 flex-1 truncate">
				{state === "assigned" && assignment?.criteriaName}
				{state === "missing" && t("leadScoring.matrix.missing")}
				{state === "none" && t("leadScoring.matrix.notExisting")}
			</span>
			<Icon className="h-3 w-3 shrink-0 text-text-disabled" icon={faChevronDown} />
		</button>
	);

	const tip =
		state === "missing"
			? t("leadScoring.matrix.missingTip")
			: state === "none"
				? t("leadScoring.matrix.notExistingTip", { persona: persona.name, segment: segment.name })
				: null;

	return (
		<>
			{tip ? (
				<Tooltip content={tip} delay={400}>
					{button}
				</Tooltip>
			) : (
				button
			)}
			{open && buttonRef.current && (
				<ScoringPicker
					anchorEl={buttonRef.current}
					isInvestorRow={segment.investorName !== null}
					linked={linked}
					options={options}
					selectedId={assignment?.criteriaId ?? null}
					onClear={() => {
						onClear(segment, persona);
						setOpen(false);
					}}
					onClose={() => setOpen(false)}
					onCreate={() => {
						onCreate(segment, persona);
						setOpen(false);
					}}
					onDeactivate={() => {
						onDeactivate(segment, persona);
						setOpen(false);
					}}
					onEdit={(criteriaId) => {
						onEdit(criteriaId);
						setOpen(false);
					}}
					onSelect={(criteriaId) => {
						onPick(segment, persona, criteriaId);
						setOpen(false);
					}}
				/>
			)}
		</>
	);
};
